import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
  IonLoading,
  IonSpinner,
  IonToast,
  IonModal,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
} from "@ionic/react";
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Autocomplete,
  CircularProgress,
  Grid,
  Alert,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, PersonAdd } from "@mui/icons-material";
import { requestID, baseUrl, config } from "../../../common/utils";
import { createCustomer } from "../../../api/customers/customers"; // Import the function

const FundCustomerWallet: React.FC = () => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({
    loading: false,
    error: false,
    message: "",
  });
  const [historyTable, setHistoryTable] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = useState(false);
  
  const [toast, setToast] = useState({
    isOpen: false,
    message: "",
    color: "success" as "success" | "danger"
  });

  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  // New customer state
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    phone: "",
    email: "",
  });
  const [isAddingCustomer, setIsAddingCustomer] = useState(false);

  useEffect(() => {
    getHistory();
  }, []);

  useEffect(() => {
    if (searchTerm.length < 3) {
      setCustomers([]);
      setOpen(false);
      return;
    }

    setIsSearching(true);
    setOpen(true);
    const handler = setTimeout(() => {
      getCustomers(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  async function getCustomers(searchQuery: string) {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("search", searchQuery);
      const response = await axios.post(
        baseUrl() + "users/getCustomers2wallet_only",
        formData,
        config
      );
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setIsSearching(false);
    }
  }

  async function getHistory() {
    setStatus({ loading: true, error: false, message: "Fetching history..." });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());

      const response = await axios.post(
        baseUrl() + "wallets/last15Transactions",
        formData,
        config
      );
      console.log(response);
      setHistoryTable(response.data);
      setStatus({ loading: false, error: false, message: "" });
    } catch (error) {
      console.error("Error fetching history:", error);
      setStatus({ loading: false, error: true, message: "" });
    }
  }

  // Add new customer function using your createCustomer API
  const handleAddCustomer = async () => {
    if (!newCustomer.firstName || !newCustomer.phone) {
      setToast({
        isOpen: true,
        message: "Please fill in at least First Name and Phone Number",
        color: "danger"
      });
      return;
    }

    setIsAddingCustomer(true);
    try {
      const response = await createCustomer(newCustomer);
      
      if (response.status === true) {
        setToast({
          isOpen: true,
          message: "Customer added successfully!",
          color: "success"
        });

        // Clear form
        setNewCustomer({
          firstName: "",
          middleName: "",
          lastName: "",
          phone: "",
          email: "",
        });

        // Close modal
        setShowAddCustomerModal(false);

        // Refresh customer list
        if (searchTerm.length >= 3) {
          await getCustomers(searchTerm);
        }

        // Auto-select the newly created customer
        const newCustomerData = response.data;
        if (newCustomerData) {
          setSelectedCustomer(newCustomerData.user_id);
          // Update the customers list to include the new customer
          setCustomers(prev => [newCustomerData, ...prev]);
          setSearchTerm(`${newCustomerData.firstName} ${newCustomerData.lastName}`);
        }
      } else {
        setToast({
          isOpen: true,
          message: response.message || "Failed to add customer",
          color: "danger"
        });
      }
    } catch (error: any) {
      console.error("Error creating customer:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to add customer. Please check inputs.";
      
      setToast({
        isOpen: true,
        message: errorMessage,
        color: "danger"
      });
    } finally {
      setIsAddingCustomer(false);
    }
  };

  async function handleFundWallet() {
    console.log({ selectedCustomer, amount, paymentMethod });
    if (!selectedCustomer || !amount || !paymentMethod) {
      setToast({
        isOpen: true,
        message: "Please fill out all fields",
        color: "danger"
      });
      return;
    }

    const customer = customers.find(
      (cust) => cust.user_id === selectedCustomer
    );
    if (!customer) {
      setToast({
        isOpen: true,
        message: "Selected customer not found",
        color: "danger"
      });
      return;
    }

    setStatus({ loading: true, error: false, message: "Funding wallet..." });
    setIsSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("customername", `${customer.firstName}`);
      formData.append("amount", amount);
      formData.append("customer_user_id", customer.user_id);
      formData.append("payment_method", paymentMethod);

      const response = await axios.post(
        baseUrl() + "wallets/fundCustomerWallet",
        formData,
        config
      );
      console.log(response);
      
      if (response.data.status === 'success') {
        setToast({
          isOpen: true,
          message: response.data.message || 'Wallet funded successfully!',
          color: 'success'
        });
        
        setSelectedCustomer("");
        setAmount("");
        setPaymentMethod("");
        setSearchTerm("");
        setCustomers([]);

        await getHistory();
      } else {
        setToast({
          isOpen: true,
          message: response.data.message || 'Failed to fund wallet',
          color: 'danger'
        });
      }
    } catch (error: any) {
      console.log(error);
      let errorMessage = 'An error occurred while funding wallet';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setToast({
        isOpen: true,
        message: errorMessage,
        color: 'danger'
      });
    }

    setStatus({ loading: false, error: false, message: "" });
    setIsSubmitting(false);
  }

  const filteredHistory = historyTable?.filter((row: any) => {
    const rowDate = new Date(row.dateCreatedx);
    const startDate = filterStartDate ? new Date(filterStartDate) : null;
    const endDate = filterEndDate ? new Date(filterEndDate) : null;

    if (startDate && rowDate < startDate) {
      return false;
    }
    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (rowDate > endOfDay) {
        return false;
      }
    }
    return true;
  });

  return (
    <IonPage className="pushDownw">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Fund Customer Wallet</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding">
        

        <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
          <Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: 600 }}>
            <Typography
              variant="h6"
              gutterBottom
              style={{
                color: "#1976d2",
                textAlign: "center",
                textTransform: "uppercase",
              }}
            >
              Fund Customer Wallet
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Select or Add Customer
                </Typography>
                <IonButton
                  size="small"
                  onClick={() => setShowAddCustomerModal(true)}
                  fill="outline"
                >
                  <PersonAdd style={{ marginRight: 8 }} />
                  Add New Customer
                </IonButton>
              </Box>

              <Autocomplete
                open={open}
                onOpen={() => {
                  if (searchTerm.length >= 3) {
                    setOpen(true);
                  }
                }}
                onClose={() => setOpen(false)}
                onInputChange={(event, newInputValue, reason) => {
                  if (reason === "input") {
                    setSearchTerm(newInputValue);
                  }
                }}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue ? newValue.user_id : "");
                  if (!newValue) {
                    setSearchTerm("");
                  }
                }}
                options={customers}
                loading={isSearching}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName ? option.lastName : ''} : ${option.phone}` || ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for a customer (min. 3 characters)"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSearching ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.user_id}>
                    {option.firstName} {option.lastName ? option.lastName : ''} : {option.phone}
                  </li>
                )}
              />
            </Box>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              margin="normal"
            />

            <TextField
              select
              fullWidth
              label="Payment Method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              margin="normal"
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="pos">POS</MenuItem>
            </TextField>

            <Box mt={2}>
              <IonButton
                expand="block"
                onClick={handleFundWallet}
                disabled={!amount || !paymentMethod || isSubmitting}
              >
                {isSubmitting ? <IonSpinner name="circles" /> : "Fund Wallet"}
              </IonButton>
            </Box>
          </Paper>
        </Box>

        {/* Add Customer Modal */}
        <IonModal isOpen={showAddCustomerModal} onDidDismiss={() => setShowAddCustomerModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Add New Customer</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAddCustomerModal(false)}>
                  <CloseIcon />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Customer Information</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Add a new customer before funding their wallet. At minimum, provide First Name and Phone Number.
                </Alert>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="First Name *"
                      value={newCustomer.firstName}
                      onChange={(e) =>
                        setNewCustomer({
                          ...newCustomer,
                          firstName: e.target.value,
                        })
                      }
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Middle Name"
                      value={newCustomer.middleName}
                      onChange={(e) =>
                        setNewCustomer({
                          ...newCustomer,
                          middleName: e.target.value,
                        })
                      }
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={newCustomer.lastName}
                      onChange={(e) =>
                        setNewCustomer({
                          ...newCustomer,
                          lastName: e.target.value,
                        })
                      }
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number *"
                      value={newCustomer.phone}
                      onChange={(e) =>
                        setNewCustomer({
                          ...newCustomer,
                          phone: e.target.value,
                        })
                      }
                      margin="normal"
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) =>
                        setNewCustomer({
                          ...newCustomer,
                          email: e.target.value,
                        })
                      }
                      margin="normal"
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  <IonButton
                    fill="outline"
                    onClick={() => setShowAddCustomerModal(false)}
                    disabled={isAddingCustomer}
                  >
                    Cancel
                  </IonButton>
                  <IonButton
                    onClick={handleAddCustomer}
                    disabled={!newCustomer.firstName || !newCustomer.phone || isAddingCustomer}
                  >
                    {isAddingCustomer ? (
                      <>
                        <IonSpinner name="circles" style={{ marginRight: 8 }} />
                        Adding...
                      </>
                    ) : (
                      "Add Customer"
                    )}
                  </IonButton>
                </Box>
              </IonCardContent>
            </IonCard>
          </IonContent>
        </IonModal>

        <IonTitle
          style={{
            color: "#1976d2",
            textAlign: "center",
            padding: "20px 0",
            textTransform: "uppercase",
          }}
        >
          Last 15 Transactions
        </IonTitle>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 3 }}>
          <TextField
            label="Start Date"
            type="date"
            variant="outlined"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <TextField
            label="End Date"
            type="date"
            variant="outlined"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
        </Box>

        {historyTable?.length > 0 && (
          <Paper
            style={{
              backgroundColor: "white",
              color: "#333",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              border: "1px solid #e0e0e0",
              margin: "0 16px 40px",
            }}
          >
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#1976d2" }}>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Full Name
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Email
                  </TableCell>
                   <TableCell style={{ color: "white", textAlign: "center" }}>
                    Staff Name
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Credited Amount (₦)
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Method of Payment
                  </TableCell>
                  <TableCell style={{ color: "white", textAlign: "center" }}>
                    Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory?.map((row: any, i: number) => (
                  <TableRow
                    key={i}
                    style={{ backgroundColor: i % 2 ? "#e2e2e2" : "white" }}
                  >
                    <TableCell style={{ textAlign: "center" }}>
                      {row.firstName}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {row.email}
                    </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                                    {row.staff_name}
                                  </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      ₦{new Intl.NumberFormat("en-NG").format(row.drValue)}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {row.shopID}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {row.dateCreatedx}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        <IonToast
          isOpen={toast.isOpen}
          message={toast.message}
          color={toast.color}
          duration={3000}
          onDidDismiss={() => setToast({ ...toast, isOpen: false })}
          position="top"
        />
      
      </IonContent>
      
      <IonLoading
        isOpen={status.loading}
        message={status.message || "Please wait..."}
        spinner="circles"
      />
    </IonPage>
  );
};

export default FundCustomerWallet;